import React from "react";
import { AlertCircle, RotateCw } from "lucide-react";

/**
 * ErrorBoundary pour les onglets admin.
 *
 * Si un onglet plante au render (undefined access, bad data venant du
 * backend, cas limite non géré), on affiche un message clair + un
 * bouton "Réessayer" au lieu d'un écran blanc. Préserve la nav : les
 * autres onglets restent cliquables, le shell admin ne bouge pas.
 */
export default class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, err: null, nonce: 0 };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, err };
  }

  componentDidCatch(err, info) {
    // Log pour pouvoir diagnostiquer plus tard. On ne crash pas l'app.
    // eslint-disable-next-line no-console
    console.error("[AdminErrorBoundary]", this.props.label || "unknown-tab", err, info?.componentStack);
  }

  reset = () => {
    this.setState((s) => ({ hasError: false, err: null, nonce: s.nonce + 1 }));
  };

  render() {
    if (this.state.hasError) {
      const msg = (this.state.err && (this.state.err.message || String(this.state.err))) || "Erreur inattendue";
      return (
        <div
          className="rounded-2xl bg-red-50 p-5 street-border-thin street-shadow-sm text-center"
          data-testid="admin-tab-error-boundary"
        >
          <AlertCircle className="mx-auto h-8 w-8 text-red-600 mb-2" strokeWidth={2.5} />
          <div className="font-[Outfit] text-[14px] font-black text-red-800 mb-1">
            Oups — cet onglet a un problème
          </div>
          <div className="text-[11.5px] text-red-700/80 mb-3 break-words">
            {msg}
          </div>
          <button
            type="button"
            onClick={this.reset}
            data-testid="admin-tab-error-retry"
            className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-[12px] font-black text-primary-foreground street-border-thin street-shadow-sm street-press"
          >
            <RotateCw className="h-3.5 w-3.5" /> Réessayer
          </button>
        </div>
      );
    }
    // key forces re-mount on retry → clear state of children
    return <React.Fragment key={this.state.nonce}>{this.props.children}</React.Fragment>;
  }
}
